// src/components/Login.js
import React, { useEffect, useMemo, useState } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { Link } from 'react-router-dom'; 
import { auth } from '../firebase';
import { authAPI } from '../services/api';
import logo from '../pages/classlabs.png'; 

// --- BRAND PALETTE ---
const BRAND = {
    teal: '#75B9BE',       
    cream: '#FDF6EC',      
    charcoal: '#2D4042',   
    coral: '#F28482',      
    white: '#FFFFFF',      
    border: '#2D4042'
};

const getFriendlyAuthError = (err) => {
  const serverError = err.response?.data?.error;
  if (serverError) return serverError;

  switch (err.code) {
    case 'auth/invalid-credential':
    case 'auth/user-not-found':
    case 'auth/wrong-password':
      return 'We could not log you in with those details. Please check your email and password.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/weak-password':
      return 'Please choose a stronger password.';
    case 'auth/network-request-failed':
      return 'We could not reach the login service. Please check your connection and try again.';
    default:
      return 'Something went wrong while signing you in. Please try again.';
  }
};

const Login = ({ onLoginSuccess, portalConfig = {} }) => {
  const {
    title,
    subtitle,
    registrationCode: fixedRegistrationCode,
    defaultMode = 'login',
    allowSignup = true,
    hideSignupToggle = false
  } = portalConfig;

  const normalizedFixedRegistrationCode = useMemo(
    () => (typeof fixedRegistrationCode === 'string' ? fixedRegistrationCode.trim() : ''),
    [fixedRegistrationCode]
  );

  const shouldLockRegistrationCode = Boolean(normalizedFixedRegistrationCode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // +++ NEW STATE FOR NAMES +++
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  
  const [registrationCode, setRegistrationCode] = useState('');
  const [isLogin, setIsLogin] = useState(defaultMode !== 'signup');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isLogin && shouldLockRegistrationCode) {
      setRegistrationCode(normalizedFixedRegistrationCode);
    }
  }, [isLogin, shouldLockRegistrationCode, normalizedFixedRegistrationCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let idToken;
      let user;

      if (isLogin) {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        idToken = await userCredential.user.getIdToken();
        localStorage.setItem('authToken', idToken);
        const profileRes = await authAPI.getProfile();
        user = profileRes.data.user;
      } else {
        // +++ PASS NAMES TO SIGNUP +++
        await authAPI.signup({ 
            email, 
            password, 
            registrationCode: shouldLockRegistrationCode ? normalizedFixedRegistrationCode : registrationCode,
            firstName, 
            lastName 
        });
        
        // Auto-login after signup
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        idToken = await userCredential.user.getIdToken();
        localStorage.setItem('authToken', idToken);
        const profileRes = await authAPI.getProfile();
        user = profileRes.data.user;
      }
      
      onLoginSuccess(idToken, user);

    } catch (err) {
      setError(getFriendlyAuthError(err));
      localStorage.removeItem('authToken');
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    if (!allowSignup) return;
    setIsLogin(!isLogin);
    setError('');
    setEmail('');
    setPassword('');
    setRegistrationCode('');
    setFirstName('');
    setLastName('');
  };

  return (
    <div style={{ 
      fontFamily: "'Open Sans', sans-serif", 
      backgroundColor: BRAND.cream, 
      minHeight: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      alignItems: 'center', 
      justifyContent: 'center',
      position: 'relative',
      overflow: 'hidden',
      padding: '20px',
      color: BRAND.charcoal
    }}>
      
      <style>
        {`
            @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@700;800;900&family=Open+Sans:wght@500;700&display=swap');

            h1, h2, h3, h4, label, button {
                font-family: 'Nunito', sans-serif;
            }

            .pattern-bg {
                background-image: radial-gradient(${BRAND.teal} 15%, transparent 16%), radial-gradient(${BRAND.coral} 15%, transparent 16%);
                background-size: 60px 60px;
                background-position: 0 0, 30px 30px;
                opacity: 0.15;
                position: absolute;
                top: 0; left: 0; right: 0; bottom: 0;
                z-index: 0;
                mask-image: linear-gradient(to bottom, black 0%, transparent 100%);
                -webkit-mask-image: linear-gradient(to bottom, black 0%, transparent 100%);
            }

            .btn-pop {
                width: 100%;
                padding: 16px;
                border-radius: 16px;
                font-weight: 800;
                font-size: 1.1rem;
                cursor: pointer;
                border: 3px solid ${BRAND.charcoal};
                box-shadow: 4px 4px 0px ${BRAND.charcoal};
                text-transform: uppercase;
                letter-spacing: 0.5px;
                transition: all 0.1s ease;
            }
            .btn-pop:hover:not(:disabled) {
                transform: translate(2px, 2px);
                box-shadow: 2px 2px 0px ${BRAND.charcoal};
            }
            .btn-pop:active:not(:disabled) {
                transform: translate(4px, 4px);
                box-shadow: 0px 0px 0px ${BRAND.charcoal};
            }
            .btn-pop:disabled {
                opacity: 0.7;
                cursor: not-allowed;
            }

            .btn-primary {
                background-color: ${BRAND.coral};
                color: white;
            }

            .input-pop {
                width: 100%;
                padding: 14px 18px;
                border-radius: 12px;
                border: 2px solid ${BRAND.charcoal};
                background-color: #F9FAFB;
                font-family: 'Open Sans', sans-serif;
                font-size: 1rem;
                font-weight: 600;
                color: ${BRAND.charcoal};
                outline: none;
                box-sizing: border-box;
                transition: all 0.2s ease;
            }
            .input-pop:focus {
                border-color: ${BRAND.teal};
                box-shadow: 0 0 0 3px rgba(117, 185, 190, 0.2);
                background-color: white;
            }

            .fun-card-login {
                background: ${BRAND.white};
                padding: 50px;
                border-radius: 24px;
                border: 3px solid ${BRAND.charcoal};
                box-shadow: 8px 8px 0px rgba(117, 185, 190, 0.4);
                width: 100%;
                max-width: 500px;
                text-align: center;
            }
            
            .row-split {
                display: flex;
                gap: 15px;
            }
            .col-half {
                flex: 1;
            }
        `}
      </style>

      {/* Background Pattern */}
      <div className="pattern-bg"></div>

      {/* Content Wrapper */}
      <div style={{ position: 'relative', zIndex: 10, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        
        {/* LOGO */}
        <div style={{ marginBottom: '30px' }}>
          <Link to="/" style={{ display: 'block', transition: 'transform 0.2s' }}>
            <img 
              src={logo} 
              alt="ClassLabs" 
              style={{ 
                height: '96px', 
                objectFit: 'contain',
                mixBlendMode: 'multiply',
                display: 'block',
                cursor: 'pointer'
              }} 
            />
          </Link>
        </div>

        {/* LOGIN / SIGNUP CARD */}
        <div className="fun-card-login">
            <h2 style={{ 
                margin: '0 0 10px 0', 
                color: BRAND.charcoal, 
                fontSize: '2.4rem', 
                fontWeight: '900' 
            }}>
                {isLogin ? 'Welcome back!' : 'Create Account'}
            </h2>
            
            <p style={{ 
                margin: '0 0 30px 0', 
                color: '#5D7375', 
                fontSize: '1.1rem',
                fontWeight: '600',
                lineHeight: '1.5'
            }}>
                {isLogin 
                    ? (title || 'Log in to continue your teaching superpowers.')
                    : (subtitle || 'Create your account in seconds.')} 
            </p>

            {error && (
                <div style={{
                    backgroundColor: '#FEF2F2',
                    color: '#DC2626',
                    padding: '12px',
                    borderRadius: '12px',
                    fontSize: '0.95rem',
                    marginBottom: '25px',
                    textAlign: 'left',
                    border: `2px solid #FECACA`,
                    fontWeight: '700'
                }}>
                    ⚠️ {error}
                </div>
            )}

            <form onSubmit={handleSubmit}>
                
                {/* +++ NEW: FIRST NAME & SURNAME (SIGNUP ONLY) +++ */}
                {!isLogin && (
                    <div className="row-split" style={{ marginBottom: '20px' }}>
                        <div className="col-half" style={{ textAlign: 'left' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.9rem', fontWeight: '800', textTransform: 'uppercase', color: '#5D7375' }}>First Name</label>
                            <input 
                                type="text" 
                                placeholder="Jane" 
                                value={firstName} 
                                onChange={(e) => setFirstName(e.target.value)} 
                                required 
                                className="input-pop"
                            />
                        </div>
                        <div className="col-half" style={{ textAlign: 'left' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.9rem', fontWeight: '800', textTransform: 'uppercase', color: '#5D7375' }}>Surname</label>
                            <input 
                                type="text" 
                                placeholder="Doe" 
                                value={lastName} 
                                onChange={(e) => setLastName(e.target.value)} 
                                required 
                                className="input-pop"
                            />
                        </div>
                    </div>
                )}

                <div style={{ marginBottom: '20px', textAlign: 'left' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.9rem', fontWeight: '800', textTransform: 'uppercase', color: '#5D7375' }}>Email Address</label>
                    <input 
                        type="email" 
                        placeholder="name@school.com" 
                        value={email} 
                        onChange={(e) => setEmail(e.target.value)} 
                        required 
                        className="input-pop"
                    />
                </div>

                <div style={{ marginBottom: '20px', textAlign: 'left' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.9rem', fontWeight: '800', textTransform: 'uppercase', color: '#5D7375' }}>Password</label>
                    <input 
                        type="password" 
                        placeholder="••••••••" 
                        value={password} 
                        onChange={(e) => setPassword(e.target.value)} 
                        required 
                        className="input-pop"
                    />
                </div>

                {!isLogin && !shouldLockRegistrationCode && (
                    <div style={{ marginBottom: '20px', textAlign: 'left' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.9rem', fontWeight: '800', textTransform: 'uppercase', color: '#5D7375' }}>School Code</label>
                        <input 
                            type="text" 
                            placeholder="Enter your registration code" 
                            value={registrationCode} 
                            onChange={(e) => setRegistrationCode(e.target.value)} 
                            required 
                            className="input-pop"
                        />
                    </div>
                )}

                <button 
                    type="submit" 
                    disabled={loading} 
                    className="btn-pop btn-primary"
                    style={{ marginTop: '10px' }}
                >
                    {loading ? 'Processing...' : (isLogin ? 'Log In' : 'Sign Up')}
                </button>
            </form>

            {!hideSignupToggle && (
            <div style={{ marginTop: '30px', fontSize: '1rem', color: BRAND.charcoal, fontWeight: '700' }}>
                {isLogin ? "New here?" : "Already have an account?"}
                <button 
                    onClick={toggleMode} 
                    style={{
                        background: 'none',
                        border: 'none',
                        color: BRAND.teal,
                        fontWeight: '800',
                        cursor: 'pointer',
                        padding: '0 0 0 6px',
                        fontSize: '1rem',
                        textDecoration: 'underline',
                        textDecorationThickness: '2px'
                    }}
                >
                    {isLogin ? 'Create Account' : 'Log In'}
                </button>
            </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default Login;
